import { useState, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { photosApi } from '../api/photos.api';
import type { JournalEntry, TripPhoto } from '../types';
import { toast } from '../components/ui/Toast';
import { entriesAfterDelete, entriesAfterUpdate, settleInBatches } from '../utils/photoSelection';

type PhotoPatch = { dayId?: string | null; activityId?: string | null };

/** No batch endpoint: the same PATCH/DELETE as one photo, four at a time. */
const CONCURRENCY = 4;

export function usePhotoBulkActions(
  tripId: string,
  setPhotos: Dispatch<SetStateAction<TripPhoto[]>>,
  setEntries: Dispatch<SetStateAction<JournalEntry[]>>,
) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);

  async function run(work: () => Promise<boolean>): Promise<boolean> {
    setBusy(true);
    try { return await work(); } finally { setBusy(false); }
  }

  /** True when every photo took the change. A function gives each photo its own patch. */
  function update(targets: TripPhoto[], patch: PhotoPatch | ((photo: TripPhoto) => PhotoPatch)): Promise<boolean> {
    return run(async () => {
      const patchFor = typeof patch === 'function' ? patch : () => patch;
      const { done, failed } = await settleInBatches(targets, CONCURRENCY, p => photosApi.update(tripId, p.id, patchFor(p)));
      const byId = new Map(done.map(p => [p.id, p]));
      setPhotos(prev => prev.map(p => byId.get(p.id) ?? p));
      setEntries(prev => entriesAfterUpdate(prev, done));
      if (failed > 0) toast(t('gallery.selection.updateFailed', { count: failed, total: targets.length }), 'error');
      else toast(t('gallery.selection.updated', { count: done.length }), 'success');
      return failed === 0;
    });
  }

  function remove(targets: TripPhoto[]): Promise<boolean> {
    return run(async () => {
      const { done, failed } = await settleInBatches(targets, CONCURRENCY, async p => {
        await photosApi.remove(tripId, p.id);
        return p.id;
      });
      const gone = new Set(done);
      setPhotos(prev => prev.filter(p => !gone.has(p.id)));
      setEntries(prev => entriesAfterDelete(prev, gone));
      if (failed > 0) toast(t('gallery.selection.deleteFailed', { count: failed, total: targets.length }), 'error');
      else toast(t('gallery.selection.deleted', { count: done.length }), 'success');
      return failed === 0;
    });
  }

  return { busy, update, remove };
}
